/**
 * A bidirection map between jsTask IDs and gTask IDs.
 *
 * This map supports creating new unique (to the map instance) jsTask IDs and
 * efficient lookup in both directions.
 */

export default class IdMap {
  constructor () {
    this._nextUniqueId = 1
    this._jsTaskToGTask = new Map()
    this._gTaskToJsTask = new Map()
  }

  _getNextJsTaskId () {
    let id = this._nextUniqueId.toString()
    this._nextUniqueId++
    return id
  }

  /**
   * Creates a new jsTask ID, maps it to the gTask ID, and returns the jsTask
   * ID. If gTask is null or undefined, then it maps to null.
   */
  _add (gTask) {
    let jsTask = this._getNextJsTaskId()
    if (gTask === undefined) {
      gTask = null
    }
    this._jsTaskToGTask.set(jsTask, gTask)
    if (gTask !== null) {
      this._gTaskToJsTask.set(gTask, jsTask)
    }
    return jsTask
  }

  /**
   * Creates a new jsTask ID that is not mapped to any gTask ID.
   */
  newJsTask () {
    return this._add(null)
  }

  /**
   * Updates the mapping. Both values must be neither null nor undefined.
   */
  update (jsTask, gTask) {
    if (jsTask == null || gTask == null) {
      return
    }
    if (this.getGTask(jsTask) === gTask) {
      // Don't bother doing anything since they're already mapped to each other.
      // This uses _jsTaskToGTask since it is always added to by _add.
      return
    }
    this._jsTaskToGTask.set(jsTask, gTask)
    this._gTaskToJsTask.set(gTask, jsTask)
  }

  /**
   * Returns true if the gTask ID exists in the map.
   */
  hasGTask (gTask) {
    return this._gTaskToJsTask.has(gTask)
  }

  /**
   * Returns true if the jsTask ID exists in the map.
   */
  hasJsTask (jsTask) {
    return this._jsTaskToGTask.has(jsTask)
  }

  /**
   * Returns the gTask corresponding to the given jsTask. If it does not exist,
   * returns undefined.
   */
  getGTask (jsTask) {
    return this._jsTaskToGTask.get(jsTask)
  }

  /**
   * Returns the jsTask corresponding to the given gTask. If it does not exist,
   * a new entry is created.
   */
  getJsTask (gTask) {
    if (!this._gTaskToJsTask.has(gTask)) {
      this._add(gTask)
    }
    return this._gTaskToJsTask.get(gTask)
  }
}
